import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  MultiSelect,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ExternalLink } from 'react-feather';
import { cohortsApi } from '../../../services/api/cohorts';
import { partnersApi } from '../../../services/api/partners';
import type { CohortWithDetails, PartnerContactWithCohortCount } from '../../../types';

interface EditPrimaryContactsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

export function EditPrimaryContactsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditPrimaryContactsModalProps) {
  const [saving, setSaving] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contacts, setContacts] = useState<PartnerContactWithCohortCount[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  // Fetch partner contacts when modal opens
  useEffect(() => {
    if (opened) {
      const fetchContacts = async () => {
        setLoadingContacts(true);
        try {
          const data = await partnersApi.listContacts(cohort.partner_id);
          setContacts(data);
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to load partner contacts',
            color: 'red',
          });
        } finally {
          setLoadingContacts(false);
        }
      };
      fetchContacts();

      // Initialize selected contacts from cohort data
      const currentContactIds = cohort.primary_contacts?.map(c => c.id) || [];
      setSelectedContactIds(currentContactIds);
    }
  }, [opened, cohort]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await cohortsApi.updatePrimaryContacts(cohort.id, selectedContactIds);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Primary contacts updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update primary contacts',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Format contacts for MultiSelect component
  const contactOptions = contacts.map(contact => ({
    value: contact.id,
    label: `${contact.first_name} ${contact.last_name}`,
    description: contact.email,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Primary Partner Contacts"
      size="md"
    >
      {loadingContacts ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            These contacts receive cohort notifications including status changes, session summaries, and survey results.
          </Text>

          {contacts.length === 0 ? (
            <Alert color="yellow" title="No contacts available">
              <Text size="sm" mb="sm">
                This partner has no contacts yet. Add contacts on the partner page to select them as primary contacts.
              </Text>
              <Button
                component={Link}
                to={`/partners/${cohort.partner_id}`}
                variant="light"
                size="xs"
                rightSection={<ExternalLink size={14} />}
              >
                Go to Partner Page
              </Button>
            </Alert>
          ) : (
            <MultiSelect
              label="Primary Partner Contacts"
              placeholder="Select contacts..."
              data={contactOptions}
              value={selectedContactIds}
              onChange={setSelectedContactIds}
              searchable
              clearable
              nothingFoundMessage="No contacts found"
            />
          )}

          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={contacts.length === 0}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default EditPrimaryContactsModal;
